import {Center, Spinner, Flex, useTheme, Image, Box} from '@chakra-ui/react';
import React, {useEffect, useState} from 'react';
import BackButton from '../../components/BackButton';
import {useFetchMembershipPlans} from '../../hooks/useMembershipPlans';
import FallbackImg from '../../assets/images/fallback.jpg';
import Header from './Header';
import {useNavigate, useSearchParams} from 'react-router-dom';
import HeroImage from '../../assets/images/memberOptionsHero.png';
import {H2, TextG10, TextG14} from '../../components/typography';
import MembershipBenefits from './MembershipBenefits';
import {BlueButton, WhiteButton} from '../../components/button';
import {getMembershipColor} from '../../utils/membership';
import {fetchMembershipStripeLinkWithAnalytics} from '../../utils/membershipCheckout';
import useAuth from '../../hooks/useAuth';
import {MembershipPlan} from '../../endpoints/interfaces/membershipPlan';
import {logAnalyticsEvent} from '../../utils/analytics';

const Memberships = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const {data, isLoading} = useFetchMembershipPlans();
  const fetchedMemberships = data?.data || [];
  const [stripeLoading, setStripeLoading] = useState(false);
  const [membershipIdSelected, setMembershipIdSelected] = useState<number>();
  const [isAnnualPaymentSelected, setIsAnnualPaymentSelected] = useState(false);
  const [memberships, setMemberships] = useState<MembershipPlan[]>([]);
  const {user} = useAuth();
  const [isPreview, setIsPreview] = useState(true);
  const [searchParams] = useSearchParams();

  const activeMembershipId = user?.activeMembershipPlan?.id;

  useEffect(() => {
    if (fetchedMemberships && fetchedMemberships.length > 0) {
      setMemberships([
        ...fetchedMemberships.map((membership) => ({
          ...membership,
          card_name: 'Monthly Plan',
          annual: false,
        })),
        {
          ...fetchedMemberships[fetchedMemberships.length - 1],
          card_name: 'Annual Plan',
          annual: true,
        },
      ]);

      if (fetchedMemberships[1]) {
        setMembershipIdSelected(fetchedMemberships[1].id);
        setIsAnnualPaymentSelected(fetchedMemberships[1]?.annual || false);
      }
    }

    if (searchParams.get('membership')) {
      setMembershipIdSelected(Number(searchParams.get('membership')));
      setIsAnnualPaymentSelected(fetchedMemberships[1]?.annual || false);
    }
  }, [fetchedMemberships, searchParams]);

  useEffect(() => {
    if (user) setIsPreview(false);
  }, [user]);

  useEffect(() => {
    if (memberships.length > 0 && !isLoading) {
      logAnalyticsEvent('view_item_list', {
        item_list_name: 'Subscription Plans'
      });
    }
  }, [memberships, isLoading]);

  if (isLoading || stripeLoading)
    return (
      <Center style={{minHeight: '100vh'}}>
        <Spinner
          thickness="4px"
          speed="0.65s"
          emptyColor={theme.colors.lightGrey}
          color={theme.colors.lightBlue}
          size="lg"
        />
      </Center>
    );

  return (
    <Box overflow="hidden">
      <H2 color="white" px="55px" mt="0.75rem" mb="0.75rem" textAlign="center">
        Join the
        <br />
        Local Insiders Club
      </H2>
      <TextG14 color="white" px="25px" textAlign="center">
        Dozens of meet-ups nearby, $100’s of local perks, and exclusive experiences await you.
      </TextG14>
      <Box
        w="100%"
        h="100%"
        pos="absolute"
        zIndex={-1}
        height="567px"
        top="220px"
        background={
          membershipIdSelected == 1
            ? 'linear-gradient(180deg, #EDFFF9 0%, #FFFFFF 100%)'
            : 'linear-gradient(180deg, rgba(241, 234, 255, 0.43) 0%, #FFFFFF 100%)'
        }></Box>
      <Flex position="absolute" top="0" left="0" height="230px" w="100vw">
        <BackButton onClick={() => navigate('/home')} m={'.75rem'} pos="relative" />
        <Image
          fallbackStrategy="onError"
          fallbackSrc={FallbackImg}
          //   src={event?.image_url || DefaultEventImage}
          src={HeroImage}
          alt="cover image"
          position="absolute"
          width="100%"
          height="100%"
          objectFit="cover"
          zIndex="-1"
          borderBottomRadius="14px"
        />
        {/* <AbsoluteCenter textAlign="center" mt="-18px">
          <TextA24 fontWeight="700" color="white">
            Membership
            <br /> Options
          </TextA24>
          <TextG16 mt="12px" whiteSpace="nowrap" color="white">
            A sub-title for pricing page is here
          </TextG16>
        </AbsoluteCenter> */}
      </Flex>
      <Header
        memberships={memberships}
        selectedMembershipId={membershipIdSelected}
        setSelectedMembershipId={setMembershipIdSelected}
        isAnnualPaymentSelected={isAnnualPaymentSelected}
        setIsAnnualPaymentSelected={setIsAnnualPaymentSelected}
      />
      <MembershipBenefits
        memberships={memberships}
        selectedMembershipId={membershipIdSelected}
        setSelectedMembershipId={setMembershipIdSelected}
        isAnnualPaymentSelected={isAnnualPaymentSelected}
      />

      <Flex
        gap="10px"
        flexDir="column"
        w="100%"
        px={4}
        justifyContent="center"
        alignItems="center"
        mb="40px"
        mt="20px">
        {isPreview ? (
          <BlueButton
            onClick={() => {
              navigate('/register?membershipPreview=true');
            }}
            transition="background 0.3s ease"
            w="100%"
            borderRadius="7px"
            background={getMembershipColor(
              membershipIdSelected == 1 ? memberships[0] : memberships[1]
            )}>
            <TextG14 fontWeight="500">Continue to Sign Up</TextG14>
          </BlueButton>
        ) : (
          <>
            <BlueButton
              onClick={async () => {
                if (membershipIdSelected) {
                  if (activeMembershipId == membershipIdSelected) {
                    return;
                  }
                  
                  // Find the selected membership to get pricing data
                  const selectedMembership = memberships.find(m => 
                    m.id === membershipIdSelected
                  );
                  
                  if (selectedMembership) {
                    const price = isAnnualPaymentSelected
                      ? +(selectedMembership.monthly_cost * 11).toFixed(2) // Annual price (1 month free)
                      : selectedMembership.monthly_cost;
                    const billingCycle = isAnnualPaymentSelected ? 'annual' : 'monthly';

                    logAnalyticsEvent('begin_checkout', {
                      currency: 'USD',
                      value: price,
                      items: [{
                      item_id: selectedMembership.id,
                      item_name: selectedMembership.name,
                      item_category: 'membership_plan',
                      item_variant: billingCycle,
                      price: price,
                      currency: 'USD',
                      quantity: 1,
                      }]
                    });
                  }
                  
                  setStripeLoading(true);

                  fetchMembershipStripeLinkWithAnalytics({
                    membershipId: membershipIdSelected,
                    annual: isAnnualPaymentSelected,
                  }).then((ret) => {
                    if (ret.data.link) {
                      window.location.href = ret.data.link;
                    } else {
                      setStripeLoading(false);
                    }
                  });
                }
              }}
              isDisabled={activeMembershipId == membershipIdSelected || activeMembershipId == 2}
              transition="background 0.3s ease"
              w="100%"
              borderRadius="7px"
              background={theme.colors.rose}>
              <TextG14 fontWeight="500">
                {activeMembershipId ? 'Upgrade Now' : 'Join the Local Insiders Club'}
              </TextG14>
            </BlueButton>
            {!activeMembershipId && (
              <Flex flexDir="column" align="center">
                <WhiteButton h={8} color={theme.colors.grey} onClick={() => navigate('/home')}>
                  Continue with Free Access
                </WhiteButton>
                <TextG10 mt="0 !important" color={theme.colors.grey}>
                  (Fewer Perks, Fewer Invites)
                </TextG10>
              </Flex>
            )}
            <TextG10 mt={2} color={theme.colors.grey}>
              Billed {isAnnualPaymentSelected ? 'annually' : 'monthly'}, cancel anytime
            </TextG10>
          </>
        )}
      </Flex>
    </Box>
  );
};

export default Memberships;
